import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Box, Typography, CircularProgress } from "@mui/material";
import api from "../../utils/api";
import { setWithExpiry, getWithExpiry } from "../../utils/cache";

import markerShadowPng from "leaflet/dist/images/marker-shadow.png";

const getMarkerIconByColor = (semanticColor) => {
  const colorMap = {
    error: "red",
    warning: "orange",
    secondary: "grey",
    success: "green",
    default: "blue",
  };

  const leafletColor = colorMap[semanticColor] || colorMap.default;

  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${leafletColor}.png`,
    shadowUrl: markerShadowPng,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    shadowAnchor: [12, 41],
  });
};

const MapBoundsController = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length === 0) return;
    const bounds = L.latLngBounds(locations.map((loc) => [loc.lat, loc.lng]));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [locations, map]);

  return null;
};

const OpenStreetMapComponent = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getWithExpiry("member-locations");
    if (cached) {
      setLocations(cached);
      setLoading(false);
      return;
    }

    api
      .get("/member-locations")
      .then((res) => {
        const data = res.data;
        const validLocations = (data.locations || []).filter(
          (loc) => loc.lat && loc.lng
        );
        setLocations(validLocations);
        setWithExpiry("member-locations", validLocations, 86400000);
      })
      .catch((err) => console.error("❌ Error loading locations:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 420,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 1.5,
          borderRadius: "14px",
          backgroundColor: "#fbfcfe",
          border: "1px solid #eef2f7",
        }}
      >
        <CircularProgress size={30} sx={{ color: "#2f63ad" }} />
        <Typography sx={{ color: "#7a8696", fontSize: "0.92rem" }}>
          Loading map...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 420,
        borderRadius: "14px",
        backgroundColor: "#fbfcfe",
        border: "1px solid #eef2f7",
        overflow: "hidden",
      }}
    >
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={5}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <MapBoundsController locations={locations} />

        {locations.map((loc) => (
          <Marker
            key={loc.ClubMemberCode}
            position={[loc.lat, loc.lng]}
            icon={getMarkerIconByColor(loc.color)}
          >
            <Popup>
              <strong>{loc.ClubMemberCode}</strong>
              <br />
              {loc.address}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Box>
  );
};

export default OpenStreetMapComponent;